import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const DAYS_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì"];
const DAYS_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven"];

const fmtAnnoScol = (a) => {
  const n = parseInt(a, 10);
  if (!n) return "—";
  return `${n}/${String((n + 1) % 100).padStart(2, "0")}`;
};

const settoreLabel = (s) => {
  const m = { A: "Infanzia", E: "Primaria", F: "Primaria", M: "Secondaria" };
  return m[(s || "").toUpperCase()] || (s || "—");
};

function classToGruppo(classe) {
  const m = String(classe || "").match(/\d+/);
  if (!m) return null;
  const n = parseInt(m[0], 10);
  if (n >= 1 && n <= 2) return 1;
  if (n >= 3 && n <= 5) return 2;
  return null;
}

function giorniToSet(g) {
  const s = new Set();
  if (g == null) return s;
  if (Array.isArray(g)) { g.forEach((x) => { const n = parseInt(x, 10); if (!isNaN(n)) s.add(n); }); return s; }
  String(g).split(/[,;\s]+/).forEach((x) => { const n = parseInt(x, 10); if (!isNaN(n)) s.add(n); });
  return s;
}

const statoOf = (it) => it.stato_iscrizione || ((it.data_recesso || "").trim() ? "recesso" : (it.in_lista_attesa ? "waiting" : "pending"));
const STATO_TXT = {
  pending: "● Inserita", waiting: "⏳ Lista attesa", in_attesa_pagamento: "💳 Link pag.",
  accettata_mail_inviata: "💳 Link pag.", confirmed: "💳 Link pag.", confermata_genitore: "💳 Link pag.",
  pagata_da_verificare: "🔍 Da verificare", pagata: "✓ Pagata", scaduta: "⏰ Scaduta",
  recesso: "⊘ Recesso", rejected: "✗ Rifiutata", chiusa: "🔒 Chiusa",
};

export default function ExtraGiorniCorsiView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [giorno, setGiorno] = useState(0); // 0=Lun ... 4=Ven
  const [filterAnno, setFilterAnno] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/mensa-matrice/extra-iscrizioni");
        setItems(r.data?.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const anni = useMemo(() => {
    const s = new Set(items.map((it) => it.anno).filter(Boolean));
    return [...s].sort((a, b) => b - a);
  }, [items]);

  // default anno = più recente
  useEffect(() => {
    if (!filterAnno && anni.length) setFilterAnno(String(anni[0]));
  }, [anni, filterAnno]);

  const byAnno = useMemo(
    () => items.filter((it) => !filterAnno || String(it.anno) === String(filterAnno)),
    [items, filterAnno]
  );

  // conteggio per ogni giorno (per i badge sulle sottotab)
  const countByDay = useMemo(() => {
    const c = [0, 0, 0, 0, 0];
    for (const it of byAnno) for (const d of giorniToSet(it.giorni)) if (d >= 0 && d <= 4) c[d] += 1;
    return c;
  }, [byAnno]);

  // alunni del giorno selezionato, raggruppati per corso
  const gruppiCorso = useMemo(() => {
    const list = byAnno.filter((it) => giorniToSet(it.giorni).has(giorno));
    const map = new Map();
    for (const it of list) {
      const cod = it.corso_codice || "—";
      if (!map.has(cod)) map.set(cod, { cod, desc: it.corso_descrizione || it.attivita || cod, items: [] });
      map.get(cod).items.push(it);
    }
    const arr = [...map.values()].sort((a, b) =>
      String(a.desc).localeCompare(String(b.desc), "it", { sensitivity: "base" })
    );
    for (const g of arr) {
      g.items.sort((a, b) => {
        const ca = String(a.classe || "").toUpperCase(), cb = String(b.classe || "").toUpperCase();
        if (ca !== cb) return ca.localeCompare(cb, "it");
        return (`${a.cognome} ${a.nome}`).toUpperCase().localeCompare((`${b.cognome} ${b.nome}`).toUpperCase(), "it");
      });
    }
    return arr;
  }, [byAnno, giorno]);

  const totGiorno = countByDay[giorno];

  return (
    <div style={{ padding: "12px 20px" }} data-testid="extra-giorni-corsi-view">
      {/* Filtro anno */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#546e7a" }}>ANNO ISCRIZIONE</label>
        <select data-testid="gc-anno" value={filterAnno} onChange={(e) => setFilterAnno(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid #b0bec5", borderRadius: 4, fontSize: 13, fontWeight: 700 }}>
          {anni.map((a) => <option key={a} value={a}>{fmtAnnoScol(a)}</option>)}
        </select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#607d8b" }}>
          Tabella alunni per giorno del corso
        </span>
      </div>

      {/* Sotto-tab giorni con conteggio in evidenza */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {DAYS_LABELS.map((lbl, d) => {
          const active = giorno === d;
          return (
            <button key={d} data-testid={`gc-day-${d}`} onClick={() => setGiorno(d)}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", cursor: "pointer",
                border: active ? "2px solid #6a1b9a" : "1px solid #cfd8dc", borderRadius: 8,
                background: active ? "#6a1b9a" : "white", color: active ? "white" : "#37474f", fontWeight: 700, fontSize: 13,
              }}>
              <span>{lbl}</span>
              <span data-testid={`gc-day-count-${d}`} style={{
                background: active ? "white" : "#ede7f6", color: active ? "#6a1b9a" : "#6a1b9a",
                fontWeight: 800, fontSize: 13, minWidth: 26, textAlign: "center", padding: "1px 7px", borderRadius: 20,
              }}>{countByDay[d]}</span>
            </button>
          );
        })}
      </div>

      {/* Intestazione giorno + totale in evidenza */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12, padding: "10px 16px", background: "#f3e5f5", borderLeft: "6px solid #6a1b9a", borderRadius: 6 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: "#4a148c" }}>🗓 {DAYS_LABELS[giorno]}</span>
        <span data-testid="gc-tot-giorno" style={{ marginLeft: "auto", fontSize: 20, fontWeight: 900, color: "white", background: "#6a1b9a", padding: "4px 18px", borderRadius: 30 }}>
          {totGiorno} alunni
        </span>
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: "center", color: "#90a4ae" }}>Caricamento…</div>
      ) : gruppiCorso.length === 0 ? (
        <div data-testid="gc-empty" style={{ padding: 30, textAlign: "center", color: "#90a4ae" }}>
          Nessun alunno con un corso il {DAYS_LABELS[giorno]}.
        </div>
      ) : (
        gruppiCorso.map((g) => (
          <div key={g.cod} data-testid={`gc-corso-${g.cod}`} style={{ marginBottom: 18, border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", background: "#eceff1" }}>
              <span style={{ fontWeight: 800, color: "#37474f", fontSize: 14 }}>📋 {g.cod} · {g.desc}</span>
              <span style={{ marginLeft: "auto", fontWeight: 800, color: "#1b5e20", background: "#e8f5e9", padding: "2px 12px", borderRadius: 20, fontSize: 13 }}>
                {g.items.length} alunni
              </span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f5f5f5", textAlign: "left", color: "#546e7a" }}>
                  <th style={th}>#</th>
                  <th style={th}>Classe</th>
                  <th style={th}>Gruppo</th>
                  <th style={th}>Alunno (Cognome Nome)</th>
                  <th style={th}>Settore</th>
                  <th style={th}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((it, i) => {
                  const st = statoOf(it);
                  const isReces = st === "recesso";
                  return (
                    <tr key={it._id} data-testid={`gc-row-${it._id}`} style={{ borderTop: "1px solid #eee", background: isReces ? "#ffebee" : (i % 2 ? "#fafafa" : "white") }}>
                      <td style={{ ...td, color: "#999", textAlign: "center" }}>{i + 1}</td>
                      <td style={{ ...td, textAlign: "center", fontWeight: 700 }}>{it.classe || "—"}</td>
                      <td style={{ ...td, textAlign: "center" }}>{classToGruppo(it.classe) ?? "—"}</td>
                      <td style={{ ...td, fontWeight: 600 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span>{(it.cognome || "") + " " + (it.nome || "")}</span>
                          {isReces && (
                            <span style={{ background: "#d32f2f", color: "white", fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 3 }}>⊘ RECESSO</span>
                          )}
                        </span>
                        <div style={{ fontSize: 10, color: "#999" }}>Cod. {it.alunno_cod ?? "—"}</div>
                      </td>
                      <td style={td}>{settoreLabel(it.settore)}</td>
                      <td style={{ ...td, fontSize: 12 }}>{STATO_TXT[st] || st}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}

const th = { padding: "8px 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 };
const td = { padding: "6px 10px" };
